import "./__styles__/Datepicker.scss";
import React, { ReactNode } from "react";
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
} from "react-hook-form";
import Wrapper from "../Wrapper";
import {
  DatepickerCore,
  DatepickerCoreProps,
  parseValue,
  toString,
} from "./Datepicker.internal";

type DatepickerProps = {
  label?: string;
  compactLabel?: boolean;
  name: string;
  value: Maybe<Date | string>;
  helperText?: Maybe<string>;
  onChange: (value: string) => void;
  size?: "smaller" | "small" | "medium" | "large";
  children?: ReactNode;
  placeholderText?: string;
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  description?: Maybe<string>;
  minDate?: Maybe<Date>;
  maxDate?: Maybe<Date>;
  tooltip?: string;
  error?: string;
} & Omit<DatepickerCoreProps, "onChange" | "value" | "error">;

export const Datepicker = ({
  label,
  compactLabel = false,
  name,
  value,
  helperText,
  error,
  onChange,
  size = "medium",
  children,
  required,
  description,
  minDate,
  maxDate,
  tooltip,
  disabled,
  readonly,
  ...props
}: DatepickerProps) => {
  return (
    <Wrapper
      label={label}
      compactLabel={compactLabel}
      name={name}
      helperText={helperText}
      error={error}
      addon={children}
      required={required}
      description={description}
      tooltip={tooltip}
      printValue={toString(parseValue(value))}
    >
      <DatepickerCore
        id={name}
        value={value}
        label={label}
        onChange={value => onChange(value ?? "")}
        size={size}
        error={!!error}
        disabled={disabled}
        readonly={readonly}
        maxDate={maxDate}
        // The default is effectively no minimum; however, the date picker doesn't like it when I set it to the year 0
        minDate={minDate}
        required={required}
        {...props}
      />
    </Wrapper>
  );
};

type ReactHookFormDatepickerProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Pick<ControllerProps<TFieldValues, TName>, "control" | "name" | "rules"> &
  Omit<DatepickerProps, "onChange" | "value" | "error"> & {
    onChange?: (value: Maybe<Date>) => void;
  };

export const ReactHookFormDatepicker = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  rules,
  required = false,
  onChange,
  size = "medium",
  label,
  compactLabel,
  description,
  children,
  ...props
}: ReactHookFormDatepickerProps<TFieldValues, TName>) => {
  return (
    <Controller<TFieldValues, TName>
      control={control}
      name={name}
      rules={rules}
      render={({ field, fieldState }) => (
        <Wrapper
          label={label}
          compactLabel={compactLabel}
          name={name}
          error={fieldState.error?.message}
          addon={children}
          required={required}
          description={description}
          printValue={toString(parseValue(field.value))}
        >
          <DatepickerCore
            id={name}
            name={name}
            value={field.value}
            label={label}
            onChange={(value: string | null) => {
              const normalizedValue = value?.trim() || null;

              field.onChange(normalizedValue);
              onChange?.(parseValue(normalizedValue));
            }}
            size={size}
            error={!!fieldState.error}
            required={required}
            {...props}
          />
        </Wrapper>
      )}
    />
  );
};
